/**
 * The account's "Display temperature in Fahrenheit" setting, applied.
 *
 * Sensors report Celsius and everything stored, exported and rule-checked stays
 * in it. Fahrenheit is a display choice, so it is resolved here — where a
 * reading becomes text — and never where one is stored.
 */

/** F = C x 9/5 + 32. */
export const celsiusToFahrenheit = (celsius) => (celsius * 9) / 5 + 32;

/**
 * Whether this user asked for Fahrenheit.
 *
 * The flag is nested: the API's UserResponse carries `settings`, and the flag
 * lives at `settings.playback.displayTemperatureAsFahrenheit`. Reading it off
 * the top level of `user` yields undefined, which reads as "Celsius" and never
 * throws — a silent way to ignore the setting.
 */
export const prefersFahrenheit = (user) => Boolean(user?.settings?.playback?.displayTemperatureAsFahrenheit);

/** The unit [displayTemperature] returns a reading in. */
export const temperatureUnit = (useFahrenheit) => (useFahrenheit ? '°F' : '°C');

/**
 * A Celsius reading in the unit the user asked for. Anything that is not a
 * finite number — an absent channel, a placeholder — passes through untouched.
 *
 * Rounded to two decimals: the conversion lands on values like
 * 72.14000000000001, which is a fact about binary floating point rather than
 * about the air, and finer than these sensors resolve.
 */
export const displayTemperature = (celsius, useFahrenheit) => {
  if (!useFahrenheit) return celsius;
  if (typeof celsius !== 'number' || !Number.isFinite(celsius)) return celsius;
  return Number(celsiusToFahrenheit(celsius).toFixed(2));
};
